import { LocationOn as LocationOnIcon } from '@mui/icons-material';
import { Box, Grid } from '@mui/material';
import useGeocoder, {
  GeocoderFilter,
  GeocoderResult,
} from '@platform-ui-common/hooks/useGeocoder.js';
import Color from '@platform-ui-common/utilities/Color.js';
import { useCallback, useEffect, useState } from 'react';
import AutocompleteInput from '../AutocompleteInput/index.js';
import Loading from '../Loading/index.js';
import Typography from '../Typography/index.js';

interface AddressAutocompleteProps {
  afterSearch?: () => void;
  disabled?: boolean;
  error?: string | boolean;
  filter?: GeocoderFilter;
  fullWidth?: boolean;
  id: string;
  label: string;
  labelFormatter?: (value: GeocoderResult) => string;
  onChange?: (value?: GeocoderResult) => void;
  readOnly?: boolean;
  required?: boolean;
  type?: 'house' | 'city';
  value?: GeocoderResult;
}

export default function AddressAutocomplete({
  afterSearch,
  disabled,
  error,
  filter,
  fullWidth = false,
  id,
  label,
  labelFormatter,
  onChange,
  readOnly,
  required,
  type = 'house',
  value,
}: AddressAutocompleteProps): JSX.Element {
  const { autocomplete } = useGeocoder();
  const [options, setOptions] = useState<GeocoderResult[]>([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(
    async (input: string): Promise<void> => {
      if (input === '') {
        setOptions([]);
        return;
      }
      setLoading(true);
      const results = await autocomplete(input, type, filter);
      setLoading(false);
      let newOptions: GeocoderResult[] = [];
      if (results) {
        newOptions = [...newOptions, ...results];
      }
      setOptions(newOptions);
    },
    [autocomplete, filter, type],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: should run when options change
  useEffect(() => {
    if (!loading && afterSearch) afterSearch();
  }, [loading, options, afterSearch]);

  const getOptionLabel = (suggestedAddress: GeocoderResult): string => {
    if (labelFormatter) return labelFormatter(suggestedAddress);
    const { address } = suggestedAddress;
    return `${address?.label ? address.label : ''}`;
  };

  return (
    <AutocompleteInput<GeocoderResult>
      id={id}
      fullWidth={fullWidth}
      label={label}
      search={search}
      options={options}
      disabled={disabled}
      required={required}
      getOptionLabel={getOptionLabel}
      renderOption={(props, option): JSX.Element => (
        <li {...props} key={option.id}>
          <Grid container alignItems="center">
            <Grid item>
              <Box component={LocationOnIcon} sx={{ color: 'text.secondary', mr: 2 }} />
            </Grid>
            <Grid item xs>
              <Typography variant="body" color={Color.TextSecondary}>
                {option.address.label}
              </Typography>
            </Grid>
          </Grid>
        </li>
      )}
      onChange={onChange}
      value={value}
      loading={loading}
      loadingComponent={
        <div className="text-center">
          <Loading size="XL" />
        </div>
      }
      readOnly={readOnly}
      error={error}
    />
  );
}
